import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, RefreshCw, CheckCircle, Trash2, File, X, Play, ArrowRight, Database, BarChart3, Settings, Zap } from 'lucide-react';
import dashboard from "../../assets/images/dashboard1.png";
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { openDB } from 'idb';

const UploadFiles = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTool, setSelectedTool] = useState("");
  const fileInputRef = useRef(null);

  // Initialize IndexedDB
  const dbPromise = openDB('AnalysisDB', 1, {
    upgrade(db) {
      // Create an object store for analyzed data and selected tool
      if (!db.objectStoreNames.contains('analysis')) {
        db.createObjectStore('analysis', { keyPath: 'id' });
      }
    },
  });

  // Auto-advance animation steps
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Update current step based on upload progress
  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setCurrentStep(0);
    } else if (uploadedFiles.some(f => f.status === 'uploading')) {
      setCurrentStep(1);
    } else if (uploadedFiles.every(f => f.status === 'completed')) {
      setCurrentStep(2);
    }
  }, [uploadedFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const items = e.dataTransfer.items;

    const files = [];

    const traverseFileTree = (item, path = '') => {
      return new Promise((resolve) => {
        if (item.isFile) {
          item.file((file) => {
            file.fullPath = path + file.name;
            files.push(file);
            resolve();
          });
        } else if (item.isDirectory) {
          const dirReader = item.createReader();
          dirReader.readEntries(async (entries) => {
            await Promise.all(entries.map((entry) => traverseFileTree(entry, path + item.name + '/')));
            resolve();
          });
        }
      });
    };

    const entries = Array.from(items)
      .map(item => item.webkitGetAsEntry())
      .filter(entry => entry); // remove nulls

    await Promise.all(entries.map(entry => traverseFileTree(entry)));

    handleFiles(files);
  }, []);


  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return ['.xml', '.json', '.csv', '.graphml'].includes(extension) && file.size <= 100 * 1024 * 1024;
    });

    setIsUploading(true);

    validFiles.forEach((file, index) => {
      setTimeout(() => {
        const newFile = {
          id: Date.now() + index,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'uploading',
          progress: 0,
          originalFile: file
        };

        setUploadedFiles(prev => [...prev, newFile]);

        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 30;
          if (progress >= 100) {
            progress = 100;
            clearInterval(interval);
            setUploadedFiles(prev =>
              prev.map(f => f.id === newFile.id ? { ...f, status: 'completed', progress: 100 } : f)
            );
            if (index === validFiles.length - 1) setIsUploading(false);
          } else {
            setUploadedFiles(prev =>
              prev.map(f => f.id === newFile.id ? { ...f, progress: Math.round(progress) } : f)
            );
          }
        }, 200);
      }, index * 300);
    });
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIconColor = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const colors = {
      'xml': '#f97316',
      'json': '#3b82f6',
      'csv': '#10b981'
    };
    return colors[extension] || '#6b7280';
  };

  const steps = [
    {
      title: "Upload your files",
      description: "Drag and drop or click to select CSV files",
      icon: <Upload size={24} />,
      color: "rgba(255, 255, 255, 0.2)"
    },
    {
      title: "Processing Data",
      description: "We're analyzing and validating your uploaded files",
      icon: <RefreshCw size={24} />,
      color: "rgba(255, 255, 255, 0.2)"
    },
    {
      title: "Ready to Analyze",
      description: "Your files are processed and ready for data analysis",
      icon: <CheckCircle size={24} />,
      color: "rgba(255, 255, 255, 0.2)"
    },
    {
      title: "Generate Insights",
      description: "Transform your data into actionable business insights",
      icon: <BarChart3 size={24} />,
      color: "rgba(255, 255, 255, 0.2)"
    }
  ];

  const features = [
    {
      icon: <Database size={20} />,
      title: "Smart Data Processing",
      description: "Automatically detect and process different file formats"
    },
    {
      icon: <BarChart3 size={20} />,
      title: "Real-time Analytics",
      description: "Get instant insights as soon as your files are uploaded"
    },
    {
      icon: <Zap size={20} />,
      title: "Lightning Fast",
      description: "Process large datasets in seconds, not minutes"
    },
    {
      icon: <Settings size={20} />,
      title: "Customizable Reports",
      description: "Tailor your analysis to meet specific business needs"
    }
  ];

  const handleAnalyzeClick = async () => {
    const formData = new FormData();

    // Collect only the original File objects from uploadedFiles
    uploadedFiles.forEach(file => {
      if (file.originalFile) {
        formData.append("files", file.originalFile);  // Backend expects "files"
      }
    });

    try {
      const token = localStorage.getItem("Token");

      const res = await axiosInstance.post(
        `${process.env.REACT_APP_BACKEND_URL}/analyze/${selectedTool}/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Store analyzed data and selected tool in IndexedDB
      const db = await dbPromise;
      await db.put('analysis', { id: 'analyzedData', data: res.data });
      await db.put('analysis', { id: 'selectedTool', data: selectedTool });


      navigate("/visualize");

    } catch (err) {
      console.error("Upload failed:", err);

      const backendError =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "An unexpected error occurred.";

      alert(`Upload failed:\n${typeof backendError === "string" ? backendError : JSON.stringify(backendError, null, 2)}`);
    }
  };


  return (
    <div className="interactive-upload-container">
      <div className="container-fluid">

        <h1 className="display-9 fw-bold mb-3 text-gradient">Code quality report</h1>
        <p className="text-muted dashboard-p fs-9">
          Upload the folder containing code quality analysis report generated by Designite tools.
        </p>
        <div className="row">
          {/* Left Column - Upload Section */}
          <div className="col-lg-6 col-md-6 col-12 col-sm-12 upload-section">
            <div className="upload-content">


              {/* Upload Area */}
              <div
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >

                <div className={`upload-icon ${isDragging ? 'dragging' : ''}`}>
                  <Upload size={32} />
                </div>

                <h3 className="upload-title">
                  {isDragging ? 'Drop your files here!' : 'Upload analysis folder'}
                </h3>
                <p className="upload-subtitle">
                  Drag and drop folder here, or click to browse
                </p>

                <div className="upload-badges">
                  {/* <span className="upload-badge xml">XML</span> */}
                  <span className="upload-badge json">.GraphML</span>
                  <span className="upload-badge csv">.CSV</span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  webkitdirectory="true"
                  directory=""
                  onChange={handleFileSelect}
                  className="upload-input"
                />

                <button className="upload-button" disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <RefreshCw size={20} className="me-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={20} className="me-2" />
                      Choose folder
                    </>
                  )}
                </button>

                <p className="upload-file-size">Maximum file size: 100MB</p>
              </div>

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="files-section mt-4">
                  <div className="row">

                    {/* Left: File Previews */}
                    <div className="col-md-8 col-sm-12 mb-3">
                      <div className="files-header">
                        <h4 className="files-title">
                          Uploaded Files ({uploadedFiles.length})
                        </h4>
                        <button
                          className="clear-button"
                          onClick={() => setUploadedFiles([])}
                        >
                          <Trash2 size={16} className="me-1" />
                          Clear All
                        </button>
                      </div>

                      <div className="files-list">
                        {uploadedFiles.map((file, index) => (
                          <div key={file.id} className="file-item">
                            <div className="file-content">
                              <div className={`file-icon ${file.status}`}>
                                {file.status === 'completed' ? (
                                  <CheckCircle size={20} />
                                ) : file.status === 'uploading' ? (
                                  <RefreshCw size={20} className="animate-spin" />
                                ) : (
                                  <File size={20} style={{ color: getFileIconColor(file.name) }} />
                                )}
                              </div>

                              <div className="file-info">
                                <div className="file-details">
                                  <div className="file-name-container">
                                    <h5 className="file-name">{file.name}</h5>
                                    <p className="file-size-text">{formatFileSize(file.size)}</p>
                                  </div>

                                  <div className="file-actions">
                                    {file.status === 'uploading' && (
                                      <span className="status-badge uploading">
                                        {file.progress}%
                                      </span>
                                    )}
                                    {file.status === 'completed' && (
                                      <span className="status-badge complete">
                                        Complete
                                      </span>
                                    )}

                                    <button
                                      className="remove-button"
                                      onClick={() => removeFile(file.id)}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                </div>

                                {file.status === 'uploading' && (
                                  <div className="progress-bar">
                                    <div className="progress-track">
                                      <div
                                        className="progress-fill"
                                        style={{ width: `${file.progress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>
                    </div>

                    {/* Right: Tool Selector & Analyze Button */}
                    <div className="col-md-4 col-sm-12">
                      <div>
                        <h4 className="files-title">
                          Generated By
                        </h4>
                        <p className="mb-2 mt-4">Indicate which tool you used to generate the uploaded report files.</p>
                        <div className="fixed-tool-panel">

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="tool"
                              id="tool-dpy"
                              value="dpy"
                              checked={selectedTool === "dpy"}
                              onChange={(e) => setSelectedTool(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="tool-dpy">
                              DPy
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="tool"
                              id="tool-designite"
                              value="designite"
                              checked={selectedTool === "designite"}
                              onChange={(e) => setSelectedTool(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="tool-designite">
                              Designite
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="tool"
                              id="tool-designitejava"
                              value="designitejava"
                              checked={selectedTool === "designitejava"}
                              onChange={(e) => setSelectedTool(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="tool-designitejava">
                              DesigniteJava
                            </label>
                          </div>

                        </div>
                      </div>

                      <div className="analyze-button-container mt-4 text-end">
                        <button
                          className="btn btn-success upload-button analyze-btn"
                          disabled={!selectedTool ||
                            uploadedFiles.length === 0 ||
                            uploadedFiles.some(f => f.status === 'uploading')
                          }
                          onClick={handleAnalyzeClick}
                        >
                          <Play size={18} className="me-2" />
                          Analyze
                        </button>
                      </div>
                    </div>


                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Right Column - Interactive Guide */}
          {/* <div className="col-lg-4 col-md-4 col-sm-12 col-12 guide-section"> */}
          <div className="col-lg-6 col-md-6 col-sm-12 col-12">
            {/* <div className="guide-content"> */}
            {/* Process Steps */}
            {/* <div className="process-steps">
                <h2 className="guide-title">How It Works</h2>
                <div className="steps-container">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step-item ${index <= currentStep ? 'active' : ''} ${
                        index === currentStep ? 'current' : ''
                      }`}
                    >
                      <div className="step-icon" style={{ backgroundColor: step.color }}>
                        {step.icon}
                      </div>
                      <div className="step-content">
                        <h4 className="step-title">{step.title}</h4>
                        <p className="step-description">{step.description}</p>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="step-connector">
                          <ArrowRight size={16} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div> */}
            <img src={dashboard} className='dashboard-img' alt="dashboard-img" />
            {/* </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadFiles;